import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@app/store';
import type { CallState } from '@entities/call-item';

import type { ConferenceUser, HistoricalCall } from './types';

/** Приоритеты: чем меньше число, тем выше элемент в списке */
const STATE_PRIORITY: Record<CallState, number> = {
	active: 0,
	incoming: 0,
	calling: 0,
	parked: 0,
	hold: 1,
	accepted: 2,
	declined: 2,
	remoteHold: 2,
} as const;

const soredCalls = (a: HistoricalCall, b: HistoricalCall) => {
	const prioDiff = STATE_PRIORITY[a.state] - STATE_PRIORITY[b.state];

	// active/calling → hold → остальные
	if (prioDiff !== 0) return prioDiff;

	// одинаковый приоритет — сортируем по времени (новее выше)
	return new Date(b.startedAt || '').getTime() - new Date(a.startedAt || '').getTime();
};

/** Базовый срез стора, где лежат звонки */
const selectCallsState = (state: RootState) => state.calls;
const selectParkingCallsState = (state: RootState) => state.callsParking;

export const selectActiveCall = createSelector([selectCallsState], (callsState) =>
	callsState.list.find((c) => ['calling', 'active', 'incoming'].includes(c.state))
);

export const selectAllAcceptedCalls = createSelector([selectCallsState], (callsState) =>
	callsState.list.filter((c) => c.state !== 'incoming')
);

export const selectAllCalls = createSelector([selectCallsState], (callsState) =>
	callsState.list.toSorted(soredCalls)
);

export const selectCurrentCall = createSelector([selectCallsState], (callsState) =>
	callsState.currentId ? (callsState.list.find((c) => c.id === callsState.currentId) ?? null) : null
);

export const selectIncomingCalls = createSelector(
	[selectAllCalls], // сначала берём всю историю
	(allCalls) => allCalls.filter((c) => c.state === 'incoming')
);

export const selectMissedCalls = createSelector(
	[selectAllCalls], // сначала берём всю историю
	(allCalls) => allCalls.filter((c) => c.state === 'declined')
);

export const selectParkingCall = selectParkingCallsState;

export const selectPendingConferenceUsers = createSelector(
	[selectCurrentCall],
	(current): ConferenceUser[] => {
		if (!current?.conferenceUsers) return [];
		return current.conferenceUsers.filter((user) => user.status === 'pending');
	}
);

export const selectVisibleConferenceUsers = createSelector(
	[selectCurrentCall],
	(current): ConferenceUser[] => {
		if (!current?.conferenceUsers) return [];
		return current.conferenceUsers.filter(
			(user) => !['disconnected', 'pending'].includes(user.status)
		);
	}
);
